import type { Vehicle } from '@repo/domain';
import { useReservationActions, useVehicles } from '@repo/domain-service';
import type { MyIcon } from '@repo/icons';
import { PlusIcon } from '@repo/icons';
import { tr } from '@repo/locale';
import { TestId } from '@repo/test-id';
import { isInFuture } from '@repo/date-time';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, InfoCard } from '@repo/ui';
import { VehicleSelectionCard } from '@repo/entities';

import Header from '@features/reservation/components/Header';
import ConsentModal from '@features/reservation/components/consent/ConsentModal';
import VehicleCreationModal from '@features/reservation/components/vehicle/VehicleCreationModal';
import ProcessProgress from '@features/reservation/components/process/ProcessProgress';
import { useReservationConsentGate } from '@features/reservation/hooks/useReservationConsentGate';
import { useReservationConsentPrefetch } from '@features/reservation/hooks/useReservationConsentPrefetch';

import PageFrame from '@shared/components/page/PageFrame';
import { selectParkStartAtTimestamp, useParkingSearchStore } from '@shared/stores/useParkingSearchStore';
import { handleRepositoryError } from '@shared/utils/handleRepositoryError';

const AddVehicleIcon: MyIcon = (props) => <PlusIcon {...props} className="text-heading h-3 w-3" />;

interface Props {
  reservationId: string;
  secondsToDeadline?: number;
  onExpire?: () => void;
}

export default function SelectVehicleContents({ reservationId, secondsToDeadline, onExpire }: Props) {
  const navigate = useNavigate();
  const testId = TestId.root('reservation.vehiclePage');

  const parkStartAtTimestamp = useParkingSearchStore(selectParkStartAtTimestamp);
  const isStartInFuture = isInFuture(parkStartAtTimestamp);

  const { vehicles, isSuccess } = useVehicles();
  const { swapVehicleInReservation, swappingVehicleInReservation } = useReservationActions();
  const [isVehicleCreationDrawerOpen, setIsVehicleCreationDrawerOpen] = useState<boolean>(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | undefined>(undefined);
  const { expectedConsents, checkingConsents, acceptingConsents, checkConsents, acceptConsents } =
    useReservationConsentGate();
  useReservationConsentPrefetch();

  useEffect(() => {
    if (isSuccess && !vehicles.length) {
      setIsVehicleCreationDrawerOpen(true);
    }
  }, [vehicles, isSuccess]);

  // Pre-select the first vehicle so the just-added (or only) car is highlighted,
  // but assignment still requires an explicit Continue click.
  useEffect(() => {
    if (selectedVehicleId === undefined && vehicles.length) {
      setSelectedVehicleId(vehicles[0].id);
    }
  }, [vehicles, selectedVehicleId]);

  const handleVehicleAdded = () => {
    // mutation in VehicleCreationDrawer auto-invalidates the vehicles query
  };

  const assignSelectedVehicle = async () => {
    if (!selectedVehicleId) return;

    try {
      await swapVehicleInReservation({ reservationId, vehicleId: selectedVehicleId });
    } catch (error) {
      handleRepositoryError(error, {
        fallback: tr('reservations:summary.vehicle_selection.single.error'),
        testId,
      });
    }
  };

  // The vehicle is assigned (which moves the flow on to the summary) only once the
  // reservation consents are settled - straight away, or after the consent modal.
  const handleContinue = async () => {
    if (!selectedVehicleId || checkingConsents) return;
    if (!(await checkConsents())) return;

    await assignSelectedVehicle();
  };

  const handleConsentAccept = async () => {
    await acceptConsents();
    await assignSelectedVehicle();
  };

  // Clicking an already-selected vehicle confirms it (works on touch and desktop alike);
  // clicking a different one just selects it.
  const handleVehicleClick = (vehicle: Vehicle) => {
    if (vehicle.id === selectedVehicleId) {
      handleContinue();
      return;
    }
    setSelectedVehicleId(vehicle.id);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <PageFrame
      testId={testId}
      onGoBack={handleBack}
      forwardButton={{
        label: tr('reservations:vehicle_page.continue_button'),
        disabled: !selectedVehicleId || swappingVehicleInReservation || checkingConsents,
        onClick: handleContinue,
      }}
      drawer={
        <>
          <VehicleCreationModal
            testId={testId}
            open={isVehicleCreationDrawerOpen}
            onOpenChange={setIsVehicleCreationDrawerOpen}
            onVehicleAdded={handleVehicleAdded}
          />
          {expectedConsents.length > 0 && (
            <ConsentModal
              testId={TestId.root('reservation.consentModal')}
              consents={expectedConsents}
              accepting={acceptingConsents}
              onAccept={handleConsentAccept}
            />
          )}
        </>
      }
    >
      <ProcessProgress
        currentStep={2}
        secondsToDeadline={secondsToDeadline}
        onExpire={onExpire}
        testId={testId.append('progress')}
      />

      <Header
        title={isStartInFuture ? tr('reservations:vehicle_page.title_reserve') : tr('reservations:vehicle_page.title')}
        subtitle={
          isStartInFuture ? tr('reservations:vehicle_page.subtitle_reserve') : tr('reservations:vehicle_page.subtitle')
        }
        testId={testId.append('title')}
      />

      <InfoCard
        testId={testId.append('defaultCarFeedback')}
        title={tr('reservations:vehicle_page.default_car_feedback.title')}
        subtitle={tr('reservations:vehicle_page.default_car_feedback.subtitle')}
      />

      <div className="mt-auto flex flex-col gap-4">
        <div className="flex flex-col gap-4" data-testid={testId.append('vehicles')}>
          {vehicles.map((vehicle, index) => (
            <VehicleSelectionCard
              className="w-full"
              isSelected={vehicle.id === selectedVehicleId}
              key={vehicle.id}
              onClick={handleVehicleClick}
              testId={testId.append('vehicles').append('vehicle').append(index.toString())}
              vehicle={vehicle}
            />
          ))}
        </div>

        <Button
          variant="outline"
          color="ghost"
          className="border-stroke-warning-subtle text-brand-strong! h-14 w-full border-2 border-dashed bg-transparent shadow-none hover:bg-transparent"
          rightIcon={AddVehicleIcon}
          testId={testId.append('addVehicleButton')}
          onClick={() => setIsVehicleCreationDrawerOpen(true)}
        >
          {tr('reservations:vehicle_page.add_new')}
        </Button>
      </div>
    </PageFrame>
  );
}
